import { fromPromise } from '@apollo/client/core';
import { setContext } from '@apollo/client/link/context';
import { ErrorResponse, onError } from '@apollo/client/link/error';
import { getMainDefinition } from '@apollo/client/utilities';
import {
  getErrorMessages, isNestKitGraphQLError, logErrorMessages,
} from '@deep/graphql-kit';
import { TokenRefreshLink } from 'apollo-link-token-refresh';

// import { useAlertNoInternet } from '@/shared/components/core/TosterCenter/CommonTosts/AlertNoInternet/useAlertNoInternet.ts';
import {
  createRefreshTokenRequest,
  generateRequestId, getAccessToken,
  getSubscriptionToken, isAccessTokenExpired,
  refreshTokens,
  removeAllTokensFromStorage,
  setAccessToken,
  setRefreshToken,
  setSubscriptionToken,
} from '@/shared/tools/jwt.ts';
import { getEnv } from '@/shared/utils/get-env.ts';
import { sendNonFatalError } from '@/core/plugins/firebase/crashlytics.ts';

export const apolloRequestIdLink = setContext((_, { headers }) => {
  return {
    headers: {
      ...headers,
      'x-request-id': generateRequestId(),
    },
  };
});

export const apolloAuthLink = setContext((_, { headers }) => {
  const definition = getMainDefinition(_.query);

  const isSubscription = (
    definition.kind === 'OperationDefinition' &&
    definition.operation === 'subscription'
  );

  const token = isSubscription ? getSubscriptionToken() : getAccessToken();

  if (!token) {
    return {
      headers,
    };
  }

  return {
    headers: {
      ...headers,
      authorization: `Bearer ${token}`,
    },
  };
});

/* REFRESH TOKEN */
const handleRefreshTokenError = (err: Error) => {
  // full control over handling token fetch Error
  console.warn('Your refresh token is invalid. Try to relogin');
  console.error(err);

  // your custom action here
  removeAllTokensFromStorage();
  // goToSignIn();
};

const refreshTokenUrl = getEnv('VITE_APP_HTTP') + '/auth/refresh-token';

/* REFRESH TOKEN */
export const apolloRefreshTokenLink = new TokenRefreshLink({
  accessTokenField: 'accessToken',
  fetchAccessToken: () => createRefreshTokenRequest(refreshTokenUrl),
  isTokenValidOrUndefined: async () => !isAccessTokenExpired() || getAccessToken() === null,
  handleResponse: () => (response: Response) => {
    return response
      .json()
      .then((body: { accessToken: string; subscriptionToken: string; refreshToken: string }) => {
        const {
          accessToken,
          subscriptionToken,
          refreshToken,
        } = body;

        setAccessToken(accessToken);
        setSubscriptionToken(subscriptionToken);
        setRefreshToken(refreshToken);

        return {
          accessToken,
        };
      });
  },
  handleFetch: (accessToken) => setAccessToken(accessToken),
  handleError: handleRefreshTokenError,
});

/* ERROR LINK */
export const apolloErrorLink = onError((error: ErrorResponse) => {
  const {
    graphQLErrors,
    operation,
    forward,
    // networkError,
  } = error;

  // якщо робиться запит на сервер, але інтернету немає, то показуємо повідомлення
  // if (networkError) {
  //   if (!navigator.onLine) {
  //     const { showAlert } = useAlertNoInternet();
  //     showAlert();
  //   }
  // }

  if (graphQLErrors) {
    const hasTokenError = graphQLErrors.some((err) => (
      isNestKitGraphQLError(err) &&
      (err.extensions.key === 'NESTKIT_AUTHN_ACCESS_TOKEN_EXPIRED' || err.extensions.key === 'NESTKIT_AUTHN_ACCESS_TOKEN_INVALID')
    ));

    if (hasTokenError) {
      return fromPromise(
        refreshTokens(refreshTokenUrl),
      ).flatMap(() => forward(operation));
    }

    const unauthenticated = graphQLErrors.some((err) => (
      isNestKitGraphQLError(err) && err.extensions.statusCode === 401 && !location.pathname.includes('/login')
    ));

    if (unauthenticated) {
      removeAllTokensFromStorage();
      // goToSignIn();

      return;
    }

    for (const err of graphQLErrors) {
      if (!isNestKitGraphQLError(err)) {
        continue;
      }

      if (err.extensions.statusCode === 404) {
        logErrorMessages(error);
      } else {
        logErrorMessages(error);
      }

      sendNonFatalError({
        message: JSON.stringify(
          Object.fromEntries(
            getErrorMessages(error).map((v, i) => [i, v]),
          ),
        ),
      });
    }
  }
});
